import React, { useState, useEffect } from "react";
import Select from "react-select";
import PermissionsGroupService from "../../../services/v2/management/management-service";
import { useTheme } from "../../../context/ThemeContext";
import { ROLE_OPTIONS } from "../../../constants/permissions";

const PermissionsAdd = ({ isOpen, onClose, onSuccess }) => {
  const [name, setName] = useState("");
  const [roles, setRoles] = useState([]);
  const [queues, setQueues] = useState([]);
  const [users, setUsers] = useState([]);

  const [allQueues, setAllQueues] = useState([]);
  const [allUsers, setAllUsers] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loadError, setLoadError] = useState(false);

  const { isDarkMode } = useTheme(); // Obtener el estado del modo oscuro

  useEffect(() => {
    setName("");
    setRoles([]);
    setQueues([]);
    setUsers([]);
    setAllQueues([]);
    setAllUsers([]);

    const fetchData = async () => {
      setLoading(true);
      setLoadError(false);
      try {
        const response = await PermissionsGroupService.getUsersAndQueues();
        setAllQueues(response.data.data.queues || []);
        setAllUsers(response.data.data.users || []);
      } catch (err) {
        setLoadError(true);
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      fetchData();
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    if (!name.trim()) {
      setError("Name is required.");
      return;
    }

    const payload = {
      name,
      roles: roles.map((role) => role.value),
      queues:
        queues.length === 0 || queues.some((q) => q.value === "all")
          ? "all"
          : queues.map((q) => q.value),
      users:
        users.length === 0 || users.some((u) => u.value === "all")
          ? "all"
          : users.map((u) => ({
              email: u.value.email,
              userIdentity: u.value.userIdentity,
              name: u.value.name,
            })),
    };

    setLoading(true);
    setError(null);
    try {
      await PermissionsGroupService.create(payload);
      onSuccess();
      onClose();
    } catch (err) {
      setError("Failed to create permissions group.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (selectedOptions, type) => {
    if (selectedOptions.some((option) => option.value === "all")) {
      if (type === "users") {
        setUsers([{ label: "All Users", value: "all" }]);
      } else if (type === "queues") {
        setQueues([{ label: "All Queues", value: "all" }]);
      }
    } else {
      if (type === "users") {
        setUsers(selectedOptions);
      } else if (type === "queues") {
        setQueues(selectedOptions);
      }
    }
  };
  const userOptions = [
    {
      label: "DEFAULT",
      options: [{ label: "All Users", value: "all" }],
    },
    {
      label: "USERS AVAILABLE",
      options: allUsers.map((user) => ({
        label: user.name,
        value: user,
        isDisabled: users.some((u) => u.value === "all"),
      })),
    },
  ];
  const queueOptions = [
    {
      label: "DEFAULT",
      options: [{ label: "All Queues", value: "all" }],
    },
    {
      label: "QUEUES AVAILABLE",
      options: allQueues.map((queue) => ({
        label: queue.startsWith("20") ? queue.slice(5) : queue,
        value: queue,
        isDisabled: queues.some((q) => q.value === "all"),
      })),
    },
  ];

  if (!isOpen) return null;

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      backgroundColor: isDarkMode
        ? state.isFocused
          ? "rgba(75, 85, 99, 1)" // Focused color (gray-700) en modo oscuro
          : "rgba(31, 41, 55, 1)" // Default dark (gray-800)
        : state.isFocused
        ? "rgba(229, 231, 235, 1)" // Focused color (gray-200) en modo claro
        : "rgba(255, 255, 255, 1)", // Default light (white)
      borderColor: state.isFocused
        ? isDarkMode
          ? "#4F46E5" // Indigo en modo oscuro
          : "#6366F1" // Indigo claro en modo claro
        : isDarkMode
        ? "#374151" // Gray-600 en modo oscuro
        : "#E5E7EB", // Gray-200 en modo claro
      boxShadow: "none",
      "&:hover": {
        borderColor: isDarkMode ? "#4F46E5" : "#6366F1", // Indigo para hover
      },
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: isDarkMode
        ? "rgba(31, 41, 55, 1)" // Fondo oscuro para el menú
        : "rgba(255, 255, 255, 1)", // Fondo claro para el menú
      color: isDarkMode ? "white" : "black",
    }),
    multiValue: (provided, { data }) => ({
      ...provided,
      backgroundColor: data.value === "all" ? "#A855F7" : "#6B7280", // Morado para "all", gris para otros
      borderRadius: "12px",
      display: "flex",
      alignItems: "center",
      padding: "0 2px",
      color: "white",
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: "white",
      fontWeight: "bold",
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: "white",
      cursor: "pointer",
      "&:hover": {
        backgroundColor: isDarkMode ? "#3730A3" : "#4F46E5", // Oscurecer morado en hover
        color: "white",
      },
    }),
    option: (provided, { data, isFocused, isDisabled }) => ({
      ...provided,
      backgroundColor: isDisabled
        ? "transparent" // Sin fondo para opciones deshabilitadas
        : isFocused
        ? "transparent" // Fondo transparente para hover limitado al contenido
        : "transparent",
      color: isDisabled
        ? isDarkMode
          ? "rgba(209, 213, 219, 1)" // Gris claro en modo oscuro
          : "rgba(156, 163, 175, 1)" // Gris claro en modo claro
        : isDarkMode
        ? "white"
        : "black",
      borderRadius: "12px",
      display: "inline-flex", // Fondo limitado al contenido
      alignItems: "center",
      padding: "8px",
      cursor: isDisabled ? "not-allowed" : "pointer",
      "&:hover": {
        backgroundColor: isDisabled
          ? "transparent" // Sin cambios para opciones deshabilitadas
          : data.value === "all"
          ? "#A855F7"
          : "#6B7280",
        color: isDisabled ? "inherit" : "white",
        width: "fit-content", // Ajustar el ancho al contenido
      },
    }),
    placeholder: (provided) => ({
      ...provided,
      color: isDarkMode ? "#9CA3AF" : "#6B7280", // Placeholder gris
    }),
    groupHeading: (provided) => ({
      ...provided,
      color: isDarkMode ? "white" : "black",
      fontWeight: "bold",
      fontSize: "14px",
      textTransform: "uppercase",
      paddingBottom: "8px", // Espaciado entre el título y las opciones
      borderBottom: `1px solid ${isDarkMode ? "#374151" : "#E5E7EB"}`, // Línea separadora
      marginBottom: "8px", // Espacio extra para separación visual
    }),
  };

  const formatOptionLabel = ({ label, shortLabel, value }, { context }) => {
    const isAll = value === "all";
    return context === "menu" ? (
      <div className="flex items-center gap-2">
        <span
          className={`rounded-full px-2 py-1 text-xs text-white ${
            isAll ? "bg-purple-500" : "bg-gray-500 "
          }`}
        >
          {shortLabel}
        </span>
        <span>{label}</span>
      </div>
    ) : (
      <span
        className={`rounded-full text-xs text-white ${
          isAll ? "bg-purple-500" : "bg-gray-500"
        }`}
      >
        {shortLabel}
      </span>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-lg flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg max-w-2xl w-full p-6">
        <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-gray-100">
          Add New Permissions Group
        </h2>
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
            Group Name
          </label>
          <input
            type="text"
            className="border rounded p-2 w-full dark:bg-gray-700 dark:text-gray-200"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
            Roles
          </label>
          <Select
            isMulti
            options={ROLE_OPTIONS}
            closeMenuOnSelect={false}
            value={roles}
            onChange={setRoles}
            className="react-select-container"
            classNamePrefix="react-select"
            styles={customStyles}
            formatOptionLabel={formatOptionLabel}
          />
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
            Queues
          </label>
          <Select
            isMulti
            options={queueOptions}
            closeMenuOnSelect={false}
            value={queues}
            onChange={(selected) => handleChange(selected, "queues")}
            isDisabled={loadError}
            className="react-select-container"
            classNamePrefix="react-select"
            styles={customStyles}
            placeholder={
              loadError
                ? "All Queues (Global permission applied)"
                : "Select Queues..."
            }
          />
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
            Users
          </label>
          <Select
            isMulti
            options={userOptions}
            value={users}
            onChange={(selected) => handleChange(selected, "users")}
            isDisabled={loadError}
            closeMenuOnSelect={false}
            className="react-select-container"
            classNamePrefix="react-select"
            styles={customStyles}
            placeholder={
              loadError
                ? "All Users (Global permission applied)"
                : "Select Users..."
            }
          />
        </div>
        {loadError && (
          <div className="text-center mb-4">
            <div className="inline-block bg-yellow-100 text-yellow-700 px-4 py-2 rounded-md text-sm font-medium">
              Unable to load queues and users. Default global permission is
              applied. For further assistance, please contact
              support@astroline.com
            </div>
          </div>
        )}
        {error && (
          <div className="text-center mb-4">
            <div className="inline-block bg-red-100 text-red-600 px-4 py-2 rounded-md text-sm font-medium">
              {error}
            </div>
          </div>
        )}
        <div className="flex justify-end gap-2 text-md">
          <button
            className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-200"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-blue-400"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PermissionsAdd;
